/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useStoreContext } from '@/contexts/StoreContext';
import { AntFlowServerError } from '@/services/dataprovider';
import { Project } from '@/types/models';

interface Props {
  visible: boolean;
  onHide: () => void;
  onSuccess: () => void;
  project: Project;
}

export const UnPublishProjectDialog: React.FC<Props> = ({ visible, onHide, onSuccess, project }) => {
  const { t } = useTranslation();

  const { antflowProcessApiEndpoint } = useStoreContext();

  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: async () => {
      return antflowProcessApiEndpoint.unpublish_project(project.path);
    },
    onSuccess: () => {
      onSuccess();
    },
    onError(error: AntFlowServerError) {
      console.error('Error unpublishing project:', error);
      const errorData = error.response?.data as { detail?: unknown } | undefined;
      const detail = errorData?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        // FastAPI validation errors: array of { type, loc, msg, input }
        setError(
          detail
            .map((d: { loc?: unknown[]; msg?: string }) => {
              const field = Array.isArray(d.loc) ? d.loc.join('.') : '';
              return field ? `${field}: ${d.msg}` : d.msg;
            })
            .filter(Boolean)
            .join('; ')
        );
      } else if (detail != null) {
        setError(JSON.stringify(detail));
      }
    },
    throwOnError: false
  });

  const handleClose = () => {
    setError(null);
    createMutation.reset();
    onHide();
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={t(['actions.cancel', 'Cancel'])}
        severity="secondary"
        size="small"
        onClick={handleClose}
        disabled={createMutation.isPending}
      />
      <Button
        label={t(['project.unpublish.dialog.unpublish_button', 'Unpublish'])}
        size="small"
        severity="danger"
        onClick={async () => createMutation.mutate()}
        disabled={createMutation.isPending}
        loading={createMutation.isPending}
      />
    </div>
  );

  return (
    <Dialog
      header={t(['project.unpublish.dialog.title', 'Unpublish Project'])}
      visible={visible}
      style={{ width: '30%' }}
      onHide={handleClose}
      footer={footer}
      closable={!createMutation.isPending}
    >
      <div>
        <div>
          <p className="text-sm text-500">
            {t([
              'project.unpublish.dialog.description',
              'Unpublishing this project will make its processes unavailable to other users.'
            ])}
          </p>
        </div>

        {createMutation.isError && (
          <div className="p-message p-message-error p-3 border-round">
            <i className="pi pi-times-circle mr-2" />
            {error || t(['notifs.error.description', 'An error occurred'])}
          </div>
        )}
      </div>
    </Dialog>
  );
};
